// Client-side visible_when evaluation, mirroring the server's evaluator
// under its visibility policy: a comparison whose referenced value is
// absent fails, so a dependent field stays hidden until its driver is
// answered. The server re-evaluates everything at completion — this copy
// only decides what to show while typing, never what counts.

import { ConditionVar, FormCondition } from "./types";

export interface ConditionScope {
  answers: Record<string, unknown>;
  context: Record<string, unknown>;
  variables: Record<string, unknown>;
}

type Operand = ConditionVar | FormCondition;

function isCondition(operand: Operand): operand is FormCondition {
  return "op" in operand;
}

function isReference(operand: Operand): boolean {
  return (
    !isCondition(operand) &&
    (operand.type === "field" ||
      operand.type === "context" ||
      operand.type === "variable")
  );
}

function resolveOperand(operand: Operand, scope: ConditionScope): unknown {
  if (isCondition(operand)) return evaluateCondition(operand, scope);
  switch (operand.type) {
    case "field":
      return scope.answers[operand.value];
    case "context":
      return scope.context[operand.value];
    case "variable":
      return scope.variables[operand.value];
    default:
      return operand.value;
  }
}

function compareChoices(left: unknown, right: unknown): boolean {
  if (Array.isArray(left)) return left.includes(right);
  return left === right;
}

function toUtcDay(value: unknown): number | null {
  if (
    !(value instanceof Date) &&
    typeof value !== "string" &&
    typeof value !== "number"
  ) {
    return null;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return Date.UTC(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
  );
}

export function evaluateCondition(
  condition: FormCondition,
  scope: ConditionScope,
): boolean {
  const vars = condition.vars ?? [];

  switch (condition.op) {
    case "always":
      return true;
    case "and":
      return (
        vars.length > 0 &&
        vars.every((v) => isCondition(v) && evaluateCondition(v, scope))
      );
    case "or":
      return vars.some((v) => isCondition(v) && evaluateCondition(v, scope));
    case "resolves": {
      const operand = vars[0];
      if (!operand || !isReference(operand)) return false;
      const value = resolveOperand(operand, scope);
      return value !== undefined && value !== null;
    }
    default:
      break;
  }

  if (vars.length !== 2) return false;
  const [left, right] = vars;
  for (const operand of [left, right]) {
    if (isReference(operand)) {
      const value = resolveOperand(operand, scope);
      if (value === undefined || value === null) return false;
    }
  }
  const leftValue = resolveOperand(left, scope);
  const rightValue = resolveOperand(right, scope);

  switch (condition.op) {
    case "is":
      return compareChoices(leftValue, rightValue);
    case "is_not":
      return !compareChoices(leftValue, rightValue);
    case "equal":
      return leftValue === rightValue;
    case "not_equal":
      return leftValue !== rightValue;
    case "begins_with":
      return (
        typeof leftValue === "string" &&
        typeof rightValue === "string" &&
        leftValue.startsWith(rightValue)
      );
    case "ends_with":
      return (
        typeof leftValue === "string" &&
        typeof rightValue === "string" &&
        leftValue.endsWith(rightValue)
      );
    case "contains":
      return (
        typeof leftValue === "string" &&
        typeof rightValue === "string" &&
        leftValue.includes(rightValue)
      );
    case "not_contains":
      return (
        typeof leftValue === "string" &&
        typeof rightValue === "string" &&
        !leftValue.includes(rightValue)
      );
    case "greater_than":
      return Number(leftValue) > Number(rightValue);
    case "greater_equal_than":
      return Number(leftValue) >= Number(rightValue);
    case "lower_than":
      return Number(leftValue) < Number(rightValue);
    case "lower_equal_than":
      return Number(leftValue) <= Number(rightValue);
    case "on":
    case "not_on":
    case "earlier_than":
    case "earlier_than_or_on":
    case "later_than":
    case "later_than_or_on": {
      const leftDay = toUtcDay(leftValue);
      const rightDay = toUtcDay(rightValue);
      if (leftDay === null || rightDay === null) return false;
      switch (condition.op) {
        case "on":
          return leftDay === rightDay;
        case "not_on":
          return leftDay !== rightDay;
        case "earlier_than":
          return leftDay < rightDay;
        case "earlier_than_or_on":
          return leftDay <= rightDay;
        case "later_than":
          return leftDay > rightDay;
        default:
          return leftDay >= rightDay;
      }
    }
    default:
      return false;
  }
}
